import { createRouter as _createRouter, createWebHistory } from "vue-router";
import { useThemeContext } from "@/core/composables";
import { buildRedirectUrl } from "@/core/utilities";
import { ROUTES } from "@/router/routes/constants";
import { useUser } from "@/shared/account";
import { mainRoutes } from "./routes";
import type { RouteRecordName } from "vue-router";
import { computedEager } from "@vueuse/core";
import { VirtoPermissions } from "@/core/enums";

// Innovadis custom: normalize returnUrl query values from router navigation guards.
function getReturnUrlFromRouteQuery(value: unknown): string | null {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value) && typeof value[0] === "string") {
    return value[0];
  }

  return null;
}

export function createRouter(options: { base: string }) {
  const { base } = options;
  const { isAuthenticated, organization, isAdministrator, operator, checkPermissions } = useUser();
  const { themeContext } = useThemeContext();

  const loginOnBehalf = computedEager<boolean>(() => checkPermissions(VirtoPermissions.LoginOnBehalf));

  const router = _createRouter({
    routes: mainRoutes,
    history: createWebHistory(base),
    scrollBehavior(to, from, savedPosition) {
      if (to.path !== from.path) {
        return savedPosition || { top: 0 };
      } else {
        return undefined;
      }
    },
  });

  router.beforeEach((to, from, next) => {
    // Protecting routes
    const unauthorizedAccessIsDenied: boolean =
      !isAuthenticated.value &&
      !to.meta.public &&
      (to.meta.requiresAuth || !themeContext.value.storeSettings.anonymousUsersAllowed);

    if (to.meta.requiredAdmin && !isAdministrator.value && !loginOnBehalf.value) {
      return next({ name: "Account" });
    }

    if (unauthorizedAccessIsDenied) {
      // save current location to return to it after sign in
      const query = buildRedirectUrl(to) || {};

      return next({
        name: ROUTES.SIGN_IN.NAME,
        query,
      });
    }

    // Protecting company routes
    if (to.meta.requiresOrganization && !organization.value) {
      return next({ name: "Account" });
    }

    // Innovadis custom: signed-in users should continue through normal returnUrl/account routing instead of being forced into the chooser.
    if (
      isAuthenticated.value &&
      Array<RouteRecordName>(
        "Home",
        ROUTES.SIGN_IN.NAME,
        "SignUp",
        "ForgotPassword",
        "ResetPassword",
        "SetPassword",
        "ConfirmInvitation",
      ).includes(to.name)
    ) {
      const routeReturnUrl = getReturnUrlFromRouteQuery(to.query.returnUrl ?? to.query.ReturnUrl);
      return next(routeReturnUrl || { name: ROUTES.CATALOG.NAME });
    }

    return next();
  });

  return router;
}
